/* Linden — side drawer.
   Shared by the menu, the search panel and the cart drawer.

   Opening a drawer traps focus inside it, marks the rest of the page inert so
   nothing behind it is reachable, and restores focus to whatever opened it on
   close. Escape and a click on the overlay both close it. */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class SideDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.opener = null;

    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]') || event.target === this.overlay) {
        event.preventDefault();
        this.close();
      }
    });

    // Any control pointing at this drawer's id opens it.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(`[aria-controls="${this.id}"]`);
      if (!trigger || !this.id) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open(opener) {
    if (this.isOpen) return;

    this.opener = opener || document.activeElement;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    this.siblings().forEach((el) => el.setAttribute('inert', ''));

    /* The drawer is visibility:hidden until [open] lands. Style recalculation is
       lazy, so focus() called right after setAttribute silently does nothing —
       the element is still hidden as far as the engine is concerned. Reading a
       layout property forces the flush first.

       Focus goes to the panel, which carries tabindex="-1" for exactly this,
       then refines to the first real control. Two steps, so focus is never left
       on <body> while the rest of the page is inert. */
    void this.offsetHeight;
    this.panel?.focus();

    requestAnimationFrame(() => {
      const target = this.panel?.querySelector('[data-drawer-initial-focus]')
        || this.panel?.querySelector(FOCUSABLE);
      if (target && this.contains(document.activeElement)) target.focus();
    });

    document.addEventListener('keydown', this.onKeydown);
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));

    if (this.opener) this.opener.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    document.body.style.overflow = '';

    this.siblings().forEach((el) => el.removeAttribute('inert'));

    document.removeEventListener('keydown', this.onKeydown);
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));

    if (this.opener) {
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
      this.opener = null;
    }
  }

  /* Everything in <body> except the branch this drawer lives in. Marked inert
     while open so screen readers and Tab both stay inside the panel.

     Shopify wraps every section in <div id="shopify-section-...">, so the
     drawer is never a direct child of <body>. Comparing against `this` would
     mark that wrapper inert — which inerts the drawer itself, leaving nothing
     on the page focusable at all. Walk up to whichever ancestor <body> owns and
     spare that one instead. */
  branchRoot() {
    let node = this;
    while (node.parentElement && node.parentElement !== document.body) {
      node = node.parentElement;
    }
    return node;
  }

  siblings() {
    const root = this.branchRoot();
    return Array.from(document.body.children).filter((el) => el !== root);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.panel) return;

    const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('side-drawer')) {
  customElements.define('side-drawer', SideDrawer);
}
